import { createContext, useCallback, useMemo, useState, useRef, useEffect, useContext } from 'react';
import { jsx, Fragment, jsxs } from 'react/jsx-runtime';

// src/gate.ts
function normaliseDomain(domain) {
  return domain.trim().replace(/^\.+/, "").replace(/\.+$/, "").toLowerCase();
}
function hostUnderDomain(host, domain) {
  const d = normaliseDomain(domain);
  const h = host.trim().toLowerCase().replace(/\.+$/, "");
  if (!d || !h) return false;
  return h === d || h.endsWith(`.${d}`);
}
function signedOutGate(input) {
  const { host, cookieDomain, app, domain } = input;
  if (!app || !cookieDomain) return "login";
  if (!hostUnderDomain(host, cookieDomain)) return "login";
  if (domain && normaliseDomain(host) === normaliseDomain(domain)) return "login";
  return "redirect";
}
function frontDoorSignInUrl(domain, currentUrl) {
  return `https://${normaliseDomain(domain)}/?next=${encodeURIComponent(currentUrl)}`;
}
function frontPageUrl(domain) {
  return `https://${normaliseDomain(domain)}/`;
}

// src/nextUrl.ts
function bare(domain) {
  return domain.trim().replace(/^\.+/, "").replace(/\.+$/, "").toLowerCase();
}
function safeNext(next, config) {
  if (typeof next !== "string" || !next.trim()) return null;
  let url;
  try {
    url = new URL(next.trim());
  } catch {
    return null;
  }
  if (url.protocol !== "https:") return null;
  if (url.username || url.password || url.port) return null;
  const domain = bare(config.domain);
  if (!domain) return null;
  const host = url.hostname.toLowerCase();
  if (host === domain) return url.href;
  const allowed = new Set(config.apps.map((a) => `${a.subdomain.trim().toLowerCase()}.${domain}`));
  return allowed.has(host) ? url.href : null;
}
var NEXT_KEY = "omni-front-door-next";
var NEXT_MAX_AGE_MS = 60 * 60 * 1e3;
function rememberNext(store, search, config, now = Date.now()) {
  const next = safeNext(new URLSearchParams(search).get("next"), config);
  if (!next) return;
  try {
    store.setItem(NEXT_KEY, JSON.stringify({ next, at: now }));
  } catch {
  }
}
function takeNext(store, config, now = Date.now()) {
  let raw = null;
  try {
    raw = store.getItem(NEXT_KEY);
    store.removeItem(NEXT_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const { next, at } = JSON.parse(raw);
    if (typeof at !== "number" || now - at > NEXT_MAX_AGE_MS) return null;
    return safeNext(typeof next === "string" ? next : null, config);
  } catch {
    return null;
  }
}

// src/build.ts
var FrontDoorBuildError = class extends Error {
  constructor(message) {
    super(`[front-door] ${message}`);
    this.name = "FrontDoorBuildError";
  }
};
function frontDoorModules(instance, app) {
  const fd = instance.frontDoor;
  if (!app) return fd?.domain && fd.home ? fd.home.modules : instance.modules;
  if (!fd?.domain) {
    throw new FrontDoorBuildError(
      `VITE_FRONT_DOOR_APP='${app}' but instance '${instance.slug}' has no frontDoor.domain`
    );
  }
  const found = (fd.apps ?? []).find((a) => a.slug === app);
  if (!found) {
    const known = (fd.apps ?? []).map((a) => a.slug).join(", ") || "none";
    throw new FrontDoorBuildError(
      `unknown app '${app}' for instance '${instance.slug}' (frontDoor.apps: ${known})`
    );
  }
  if (!found.modules || found.modules.length === 0) {
    throw new FrontDoorBuildError(`app '${app}' of instance '${instance.slug}' lists no modules`);
  }
  return found.modules;
}
function applyFrontDoorBuild(instance, app) {
  if (!instance.frontDoor?.domain && !app) return instance;
  const modules = frontDoorModules(instance, app);
  return Object.freeze({ ...instance, modules: [...modules], navOrder: [...modules] });
}

// src/access.ts
var ACCESS_RECHECK_MS = 6e4;
function isMissingFunction(e) {
  if (!e) return false;
  return e.code === "PGRST202" || e.code === "42883" || e.code === "3F000" || /could not find the function|does not exist/i.test(e.message ?? "");
}
var FrontDoorError = class extends Error {
  code;
  /** True when the function itself is missing (the list is not switched on yet). */
  missing;
  constructor(fn, e) {
    super(`${fn}: ${e.message ?? "the database did not answer"}`);
    this.name = "FrontDoorError";
    this.code = e.code;
    this.missing = isMissingFunction(e);
  }
};
function str(v) {
  return typeof v === "string" && v.length > 0 ? v : null;
}
function rows(data) {
  if (Array.isArray(data)) return data.filter((r) => !!r && typeof r === "object");
  return data && typeof data === "object" ? [data] : [];
}
async function fetchMyApps(db) {
  const { data, error } = await db.rpc("front_door_my_apps");
  if (error) throw new FrontDoorError("front_door_my_apps", error);
  return rows(data).map((r) => ({
    slug: String(r.slug ?? ""),
    name: String(r.name ?? r.slug ?? ""),
    subdomain: str(r.subdomain),
    url: str(r.url),
    status: String(r.status ?? "live"),
    description: str(r.description),
    sort: typeof r.sort === "number" ? r.sort : null
  })).filter((a) => a.slug);
}
async function fetchCanOpen(db, appSlug) {
  const { data, error } = await db.rpc("front_door_can_open", { app_slug: appSlug });
  if (error) throw new FrontDoorError("front_door_can_open", error);
  return data === true;
}
async function fetchMe(db) {
  const { data, error } = await db.rpc("front_door_me");
  if (error) throw new FrontDoorError("front_door_me", error);
  const row = rows(data)[0];
  if (!row) return null;
  return {
    displayName: str(row.display_name),
    email: str(row.email),
    roles: Array.isArray(row.roles) ? row.roles.map(String) : [],
    isAdmin: row.is_admin === true
  };
}
function frontPageTiles(apps) {
  return apps.filter((a) => a.status === "live" || a.status === "building").slice().sort((a, b) => (a.sort ?? 999) - (b.sort ?? 999) || a.name.localeCompare(b.name));
}
function appHref(app, domain) {
  if (app.url) return app.url;
  const d = domain.trim().replace(/^\.+/, "").toLowerCase();
  if (app.subdomain && d) return `https://${app.subdomain}.${d}/`;
  return null;
}
function guardView(state) {
  if (state.status === "loading") return "loading";
  if (state.status === "error") return "error";
  return state.canOpen ? "allow" : "deny";
}
function watchAccess(host, check, ms = ACCESS_RECHECK_MS) {
  const onFocus = () => check();
  host.addEventListener("focus", onFocus);
  const id = host.setInterval(check, ms);
  return () => {
    host.removeEventListener("focus", onFocus);
    host.clearInterval(id);
  };
}
function useLoad(load) {
  const [state, setState] = useState({ status: "loading" });
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => {
    setState({ status: "loading" });
    setTick((t) => t + 1);
  }, []);
  useEffect(() => {
    if (!load) return;
    let live = true;
    load().then(
      (data) => live && setState({ status: "ready", data }),
      (error) => live && setState({ status: "error", error: error instanceof Error ? error : new Error(String(error)) })
    );
    return () => {
      live = false;
    };
  }, [load, tick]);
  return { ...state, reload };
}
var FrontDoorContext = createContext(null);
function FrontDoorProvider({ rpc, children }) {
  const loadMe = useCallback(() => fetchMe(rpc), [rpc]);
  const loadApps = useCallback(() => fetchMyApps(rpc), [rpc]);
  const me = useLoad(loadMe);
  const myApps = useLoad(loadApps);
  const value = useMemo(() => ({ rpc, me, myApps }), [rpc, me, myApps]);
  return /* @__PURE__ */ jsx(FrontDoorContext.Provider, { value, children });
}
function useFrontDoor() {
  const v = useContext(FrontDoorContext);
  if (!v) throw new Error("[front-door] hooks need <FrontDoorProvider> above them");
  return v;
}
function useMyApps() {
  return useFrontDoor().myApps;
}
function useMe() {
  return useFrontDoor().me;
}
function useCanOpen(appSlug) {
  const { rpc } = useFrontDoor();
  const [state, setState] = useState({ status: "loading" });
  const seq = useRef(0);
  const check = useCallback(() => {
    const mine = ++seq.current;
    fetchCanOpen(rpc, appSlug).then(
      (canOpen) => mine === seq.current && setState({ status: "ready", canOpen }),
      (error) => mine === seq.current && setState(
        (prev) => (
          // A failed re-check after a yes keeps the page; the database still refuses rows.
          prev.status === "ready" && prev.canOpen ? prev : { status: "error", error: error instanceof Error ? error : new Error(String(error)) }
        )
      )
    );
  }, [rpc, appSlug]);
  useEffect(() => {
    check();
    if (typeof window === "undefined") return;
    return watchAccess(window, check);
  }, [check]);
  const reload = useCallback(() => {
    setState({ status: "loading" });
    check();
  }, [check]);
  return { ...state, reload };
}
function RequireAppAccess(props) {
  const access = useCanOpen(props.app);
  const view = guardView(access);
  if (view === "allow") return /* @__PURE__ */ jsx(Fragment, { children: props.children });
  if (view === "loading") return /* @__PURE__ */ jsx(Fragment, { children: props.renderLoading ? props.renderLoading() : null });
  if (view === "error") {
    return /* @__PURE__ */ jsx(Fragment, { children: props.renderError ? props.renderError(access.reload) : /* @__PURE__ */ jsxs("div", { role: "alert", style: { padding: 32, textAlign: "center" }, children: [
      /* @__PURE__ */ jsx("p", { children: "We could not check your access just now." }),
      /* @__PURE__ */ jsx("button", { type: "button", onClick: access.reload, children: "Try again" })
    ] }) });
  }
  return /* @__PURE__ */ jsx(Fragment, { children: props.renderDenied ? props.renderDenied(props.frontPageUrl) : /* @__PURE__ */ jsxs("div", { role: "alert", style: { padding: 32, textAlign: "center" }, children: [
    /* @__PURE__ */ jsx("p", { children: "You do not have access to this app." }),
    /* @__PURE__ */ jsx("a", { href: props.frontPageUrl, children: "Go to the front page" })
  ] }) });
}

export { ACCESS_RECHECK_MS, FrontDoorBuildError, FrontDoorError, FrontDoorProvider, NEXT_KEY, RequireAppAccess, appHref, applyFrontDoorBuild, fetchCanOpen, fetchMe, fetchMyApps, frontDoorModules, frontDoorSignInUrl, frontPageTiles, frontPageUrl, guardView, hostUnderDomain, isMissingFunction, normaliseDomain, rememberNext, safeNext, signedOutGate, takeNext, useCanOpen, useMe, useMyApps, watchAccess };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map